/// <amd-module name="Artifacts/FeedSettings/components/AddEditViewPanel" />
import * as Res from "Artifacts/FeedSettings/Resources";
import {
  AddEditViewFormState,
  useAddEditViewForm,
} from "Artifacts/FeedSettings/hooks/AddEditViewForm";
import { IFeedSettings } from "Artifacts/FeedSettings/types";
import { ComponentState } from "Artifacts/Root/Constants";
import * as RootRes from "Artifacts/Root/Resources";
import { CenteredSpinner } from "Artifacts/Root/components/CenteredSpinner";
import { ErrorBannerLoader } from "Artifacts/Root/components/ErrorBannerLoader";
import { FWLink } from "Artifacts/Root/components/FWLink";
import { getCurrentCollectionName } from "Artifacts/Root/helpers/CollectionHelpers";
import { isLoading } from "Artifacts/Root/helpers/ComponentStateHelpers";
import { isPublicFeed } from "Artifacts/Root/helpers/FeedHelpers";
import { IFeed } from "Artifacts/Root/types";
import {
  FeedPermission,
  FeedRole,
  FeedView,
  FeedViewType,
  FeedVisibility,
} from "Artifacts/Root/web-apis/contracts/Feed";
import * as StringUtils from "VSS/Core/Util/String";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { useComponentContext } from "VSS/Platform/Layout";
import { Checkbox } from "VSSUI/Checkbox";
import { IIdentity, IdentityPicker } from "VSSUI/IdentityPicker";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { TextFieldWithMessage } from "VSSUI/TextField";
import * as React from "react";

export interface IAddEditViewPanelProps {
  viewToEdit?: FeedView;
  feed: IFeed;
  settings: IFeedSettings;
  onDismiss: () => void;
  onSave: (view: FeedView, feedPermsToSet: FeedPermission[], isDefaultEnabled: boolean) => void;
  saveButtonComponentState: ComponentState;
  currentViewPermissions: FeedPermission[];
  supportServicePrincipals: boolean;
}

const AddEditViewPanel: React.FC<IAddEditViewPanelProps> = ({
  settings,
  viewToEdit,
  feed,
  onDismiss,
  onSave,
  saveButtonComponentState,
  currentViewPermissions,
  supportServicePrincipals,
}) => {
  const { pageContext } = useComponentContext();
  const {
    formState,
    onNameChange,
    isFormDirty,
    isFormValid,
    onFeedVisibilityChange,
    onIsDefaultChange,
    onIdentitiesRemoved,
    onIdentityAdded,
    onIdentityRemoved,
  } = useAddEditViewForm(settings, viewToEdit, currentViewPermissions);

  const peoplePickerProvider = new PeoplePickerProvider(pageContext, {
    identityTypes: supportServicePrincipals
      ? ["user", "group", "servicePrincipal"]
      : ["user", "group"],
    operationScopes: ["ims"],
    options: {
      MinResults: 5,
      MaxResults: 20,
    },
  });

  const isProjectUpstreamsEnabled = isFeatureFlagEnabled(
    pageContext,
    "Packaging.Feed.ProjectScopedUpstreams",
  );

  return (
    <Panel
      onDismiss={onDismiss}
      titleProps={{ text: Res.FeedSettings_ViewPanel_AddHeader }}
      overlayContent={
        isLoading(saveButtonComponentState) ? (
          <CenteredSpinner label={RootRes.Submitting} />
        ) : undefined
      }
      footerButtonProps={[
        { text: Res.FeedSettings_CancelButton_Label, onClick: onDismiss },
        {
          text: Res.FeedSettings_SaveButton_Label,
          primary: true,
          disabled: !isFormDirty || !isFormValid,
          onClick: () => {
            onSave(
              formatViewToSave(viewToEdit, formState),
              identitiesToFeedPermission(formState.identities),
              formState.isDefaultView,
            );
          },
        },
      ]}
    >
      <ErrorBannerLoader state={saveButtonComponentState} noLoadingSpinner>
        <div className="flex flex-column add-edit-view-panel">
          <div className="flex flex-column">
            <TextFieldWithMessage
              message={!isFormValid ? Res.FeedSettings_ViewPanel_Error_InvalidName : undefined}
              error={!isFormValid}
              className="required"
              textFieldProps={{
                label: Res.FeedSettings_ViewPanel_TextField_Label,
                className: "view-name",
                required: true,
                value: formState.viewName,
                onChange: (_event, value) => onNameChange(value),
                disabled: viewToEdit && viewToEdit.type === FeedViewType.Implicit,
              }}
            />
          </div>
          <div className="flex flex-column">
            {isPublicFeed(feed) ? (
              <div>
                <div>{Res.FeedSettings_ViewPanel_Visibility}</div>
                <MessageCard severity={MessageCardSeverity.Info} className="public-feed-message">
                  {Res.FeedSettings_Views_PublicFeedPermissionMessage}
                </MessageCard>
              </div>
            ) : (
              <RadioButtonGroup
                text={Res.FeedSettings_ViewPanel_Visibility}
                selectedButtonId={formState.feedVisibility.toString()}
                onSelect={(value: string) => {
                  onFeedVisibilityChange(Number(value) as FeedVisibility);
                }}
              >
                <div className="visibility-type-subtext">
                  {StringUtils.format(Res.FeedSettings_ViewPanel_FeedReaders, feed.name)}
                </div>
                {getPrivateVisibilityRadioOptions(
                  feed,
                  pageContext,
                  settings.isOrgAadBacked,
                  isProjectUpstreamsEnabled,
                )}
              </RadioButtonGroup>
            )}
          </div>
          {formState.feedVisibility === FeedVisibility.Private && !isPublicFeed(feed) && (
            <div className="flex flex-column">
              <div className="visibility-type-subtext margin-bottom-4">
                <MessageCard severity={MessageCardSeverity.Warning}>
                  <FormatComponent format={Res.FeedSettings_ViewPanel_Visibility_Private_Warning}>
                    <FWLink linkId="2130515" underline>
                      {Res.FeedSettings_ViewPanel_Visibility_Permission_Details}
                    </FWLink>
                  </FormatComponent>
                </MessageCard>
              </div>
              <IdentityPicker
                onIdentityAdded={onIdentityAdded}
                onIdentityRemoved={onIdentityRemoved}
                onIdentitiesRemoved={onIdentitiesRemoved}
                selectedIdentities={formState.identities}
                pickerProvider={peoplePickerProvider}
              />
            </div>
          )}
          <div className="flex flex-column">
            <Checkbox
              className={"default-view-checkbox"}
              label={Res.FeedSettings_ViewPanel_DefaultView_Label}
              checked={formState.isDefaultView}
              onChange={(_event, value) => onIsDefaultChange(value)}
              disabled={viewToEdit ? !(settings.defaultViewId !== viewToEdit.id) : false}
            />
          </div>
        </div>
      </ErrorBannerLoader>
    </Panel>
  );
};

function getPrivateVisibilityRadioOptions(
  feed: IFeed,
  pageContext: IVssPageContext,
  aadUpstreamsEnabled: boolean,
  isProjectUpstreamsEnabled: boolean,
): JSX.Element[] {
  const options: JSX.Element[] = [];

  if (aadUpstreamsEnabled) {
    const aadVisibilityOptionElement = getVisibilityOptionElement(
      FeedVisibility.AadTenant.toString(),
      Res.FeedSettings_ViewPanel_Visibility_AadTenant,
      !!feed.project && !isProjectUpstreamsEnabled,
    );

    options.push(aadVisibilityOptionElement);
  }

  const collectionVisibilityOptionElement = getVisibilityOptionElement(
    FeedVisibility.Collection.toString(),
    StringUtils.format(
      Res.FeedSettings_ViewPanel_Visibility_Collection,
      getCurrentCollectionName(pageContext),
    ),
    !!feed.project && !isProjectUpstreamsEnabled,
  );

  const privateVisibilityOptionElement = getVisibilityOptionElement(
    FeedVisibility.Private.toString(),
    Res.FeedSettings_ViewPanel_Visibility_Private,
    false,
  );

  options.push(collectionVisibilityOptionElement);
  options.push(privateVisibilityOptionElement);

  return options;
}

function getVisibilityOptionElement(
  visibility: string,
  text: string,
  disabled: boolean,
): JSX.Element {
  return (
    <RadioButton key={visibility} id={visibility} text={text} disabled={disabled}></RadioButton>
  );
}

function formatViewToSave(
  editView: FeedView | undefined,
  formState: AddEditViewFormState,
): FeedView {
  return {
    id: editView ? editView.id : StringUtils.newGuid(),
    name: formState.viewName,
    type: editView ? editView.type : FeedViewType.Release,
    visibility: formState.feedVisibility,
    _links: editView ? editView._links : undefined,
    url: editView ? editView.url : "",
  };
}

function identitiesToFeedPermission(identities: IIdentity[]): FeedPermission[] {
  const feedPerms = identities.map((identity) => {
    return {
      displayName: identity.displayName,
      identityId: identity.localId,
      isInheritedRole: false,
      role: FeedRole.Reader,
    } as FeedPermission;
  }) as FeedPermission[];

  return feedPerms;
}

export { AddEditViewPanel };
